import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon, type IconGlyph } from '@sunwebgroup/uds-ui/icon'
import { RichText } from '@sunwebgroup/uds-ui/rich-text'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useIntl } from '@/intl'
import styles from './CheckoutSection.module.css'

interface CheckoutSectionMoreInformationProps {
  title: string
  description?: string
  descriptionElement?: React.ReactNode
  buttonAriaLabel?: string | undefined
  readMoreLabel?: string | undefined
}

interface CheckoutSectionDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {}

interface CheckoutSectionProps {
  title: string
  description?: string
  glyph?: IconGlyph
  children?: React.ReactNode
  moreInformationTitle?: string | undefined
  moreInformationDescription?: string | undefined
  moreInformationDescriptionElement?: React.ReactNode | undefined
  moreInformationButtonAriaLabel?: string | undefined
  moreInformationReadMoreLabel?: string | undefined
  id?: string | undefined
}

export function Description({ className, children, ...props }: CheckoutSectionDescriptionProps) {
  return (
    <div className={cn(styles.description, className)} {...props}>
      {children}
    </div>
  )
}

export function CheckoutSection({
  children,
  title,
  description,
  glyph,
  moreInformationTitle,
  moreInformationDescription,
  moreInformationDescriptionElement,
  moreInformationButtonAriaLabel,
  moreInformationReadMoreLabel,
  id,
}: CheckoutSectionProps) {
  return (
    <div className={styles.wrapper} data-qa="checkout-service" id={id}>
      {(title || moreInformationTitle || glyph) && (
        <div className={styles.heading}>
          {glyph && <Icon glyph={glyph} size={24} />}
          <h2 className={styles.title}>
            {title}
          </h2>
        </div>
      )}
      { (description || (moreInformationTitle && (moreInformationDescription || moreInformationDescriptionElement))) && (
        <Description>
          {description && (
            <Typography className={styles.descriptionText} dangerouslySetInnerHTML={{ __html: description }} />
          )}
          {moreInformationTitle && (moreInformationDescription || moreInformationDescriptionElement) && (
            <MoreInformationSheet
              title={moreInformationTitle}
              description={moreInformationDescription}
              descriptionElement={moreInformationDescriptionElement}
              buttonAriaLabel={moreInformationButtonAriaLabel}
              readMoreLabel={moreInformationReadMoreLabel}
            />
          )}
        </Description>
      )}

      {children && (
        <div className={styles.body}>{children}</div>
      )}
    </div>
  )
}

function MoreInformationSheet({ title, description, descriptionElement, buttonAriaLabel, readMoreLabel }: CheckoutSectionMoreInformationProps) {
  const intl = useIntl()

  return (
    <Sheet.Root>
      <Sheet.Trigger>
        <Button className={styles.readMore} variant="transparentNeutral" aria-label={buttonAriaLabel}>
          <Typography as="span">{readMoreLabel ?? intl.formatMessage({ id: 'checkout_steps_read_more' })}</Typography>
        </Button>
      </Sheet.Trigger>
      <Sheet.Body side="right" hasPadding bottomSideInMobile>
        <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={title} />
        <Sheet.Content className={styles.sheetContent}>
          {description && <RichText innerHTML={description} />}
          {descriptionElement}
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  )
}
